/**
 * QUOTATION ITEM THUNKS.
 *
 * Ogni scrittura di una riga cambia il totale del preventivo — lo ricalcola il
 * servizio core sommando le righe — quindi chi ha il preventivo a video deve
 * rileggerlo dopo. Non lo fanno questi thunk: il totale è del preventivo, non
 * della riga, e farlo aggiornare da qui significherebbe che una slice ne scrive
 * un'altra. Lo fa il componente che possiede entrambi.
 */

import { createAsyncThunk } from '@reduxjs/toolkit';
import { extractError } from '../../store/thunkHelpers';
import { quotationItemApi } from './quotationItemApi';
import type { QuotationItem } from './quotationItemMappers';

type ItemPatch = Partial<Pick<QuotationItem, 'position' | 'quantity' | 'amount' | 'notes'>>;

export const fetchQuotationItems = createAsyncThunk<
  { quotationId: number; items: QuotationItem[] },
  number
>(
  'quotationItems/fetchByQuotation',
  async (quotationId, { rejectWithValue }) => {
    try {
      const page = await quotationItemApi.getByQuotation(quotationId);
      return { quotationId, items: page.items };
    } catch (err) {
      return rejectWithValue(extractError(err));
    }
  },
);

export const createQuotationItem = createAsyncThunk<
  QuotationItem,
  { quotationId: number; payload: ItemPatch }
>(
  'quotationItems/create',
  async ({ quotationId, payload }, { rejectWithValue }) => {
    try {
      return await quotationItemApi.create(quotationId, payload);
    } catch (err) {
      return rejectWithValue(extractError(err));
    }
  },
);

export const updateQuotationItem = createAsyncThunk<
  QuotationItem,
  { quotationId: number; quotationItemId: number; patch: ItemPatch }
>(
  'quotationItems/update',
  async ({ quotationId, quotationItemId, patch }, { rejectWithValue }) => {
    try {
      return await quotationItemApi.update(quotationId, quotationItemId, patch);
    } catch (err) {
      return rejectWithValue(extractError(err));
    }
  },
);

export const reorderQuotationItems = createAsyncThunk<
  { quotationId: number; items: QuotationItem[] },
  { quotationId: number; quotationItemIds: number[] }
>(
  'quotationItems/reorder',
  async ({ quotationId, quotationItemIds }, { rejectWithValue }) => {
    try {
      const items = await quotationItemApi.reorder(quotationId, quotationItemIds);
      return { quotationId, items };
    } catch (err) {
      return rejectWithValue(extractError(err));
    }
  },
);

export const deleteQuotationItem = createAsyncThunk<
  { quotationId: number; quotationItemId: number },
  { quotationId: number; quotationItemId: number }
>(
  'quotationItems/delete',
  async ({ quotationId, quotationItemId }, { rejectWithValue }) => {
    try {
      await quotationItemApi.remove(quotationId, quotationItemId);
      return { quotationId, quotationItemId };
    } catch (err) {
      return rejectWithValue(extractError(err));
    }
  },
);
